import ConfigurationTableSearch, { matchesParameterName } from './ConfigurationTableSearch';
import React, { useState, useEffect, useRef } from 'react';
import { PlusCircle, X, ChevronDown, ChevronUp, Edit2, Trash2, Save, Upload, Download } from 'lucide-react';
import server from '../../api/server';
import "../loader.css"
import { FaRegEdit, FaTimes } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx'
import { IoChatbubbleOutline } from 'react-icons/io5';
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import ModernTimeDropdown from '../layouts/modernDropdown.jsx';
import { MdDelete } from 'react-icons/md';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const ConcernInsightConfigure = ({clientID, serviceID, module_name, setCloseModal, config_data}) => {
  const [tableSearch, setTableSearch] = useState({});
  const { showToast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [current_version, setCurrent_version] = useState(0)
  const [activeTab, setActiveTab] = useState('concerns');
  const [expandedSample, setExpandedSample] = useState(null);
  const [moduleActive, setModuleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [defaultActive, setDefaultActive] = useState(false)
  const [configType, setConfigType] = useState('Live Version')
  const [showConfigDropdown, setShowConfigDropdown] = useState(false)
  const [moduleSaveDraftConfiguration, setModuleSaveDraftConfiguration] = useState(null)
  const [showSampleTest, setShowSampleTest] = useState(false)
  const [showSampleTestModal, setShowSampleTestModal] = useState(false)
  // Training Materials state
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [showTrainingMaterialForm, setShowTrainingMaterialForm] = useState(false);
  const [editingTrainingIndex, setEditingTrainingIndex] = useState(null);
  const [selectedTrainingParamDisplay, setSelectedTrainingParamDisplay] = useState(''); // 'Concerns' | 'Queries'
  const [selectedTrainingParamKey, setSelectedTrainingParamKey] = useState(''); // 'concerns' | 'queries'
  const [materialRows, setMaterialRows] = useState([]);
  const trainingFormRef = useRef(null);
  const sourceClickInProgressRef = useRef(false);
  const [concernsData, setConcernsData] = useState({
    category: "Appointment Process & Booking",
    subcategory: "Cancellation Issues",
    example: "Customer is upset because they were charged a cancellation fee despite rescheduling more than 24 hours in advance."
  });
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  
  // New state for hierarchical structure
  const [concernsInstructions, setConcernsInstructions] = useState("");
  const [queriesInstructions, setQueriesInstructions] = useState("");
  const [concerns, setConcerns] = useState([]);
  const [queries, setQueries] = useState([]);
  
  // State for adding new items
  const [newConcern, setNewConcern] = useState("");
  const [newQuery, setNewQuery] = useState("");
  const [newSubconcern, setNewSubconcern] = useState({
    concernId: null,
    name: ""
  });
  const [newSubquery, setNewSubquery] = useState({
    queryId: null,
    name: ""
  });
  
  // State for editing
  const [editMode, setEditMode] = useState({
    type: null, // "concern", "subconcern", "query", "subquery"
    id: null,
    name: "",
    parentId: null
  });
  
  // Upload and Download Configuration Functions
  const handleDownloadConfig = () => {
    try {
      const configData = {
        concernsData: concernsData,
        concernsInstructions: concernsInstructions,
        queriesInstructions: queriesInstructions,
        concerns: concerns,
        queries: queries,
        training_materials: serializeTrainingMaterials(),
        moduleActive: moduleActive
      };

      const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `concern_insights_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        setLoading(true);
        const uploadedConcerns = {};
        (configData.concerns || []).forEach(concern => {
          uploadedConcerns[concern.name] = {
            subcategories: (concern.subconcerns || []).map(sub => sub.name.trim())
          };
        });
        const uploadedQueries = {};
        (configData.queries || []).forEach(query => {
          uploadedQueries[query.name] = {
            subcategories: (query.subqueries || []).map(sub => sub.name.trim())
          };
        });
        const validationConfig = {
          configuration: {
            concerns: {
              categories: uploadedConcerns,
              instruction: configData.concernsInstructions
            },
            queries: {
              categories: uploadedQueries,
              instruction: configData.queriesInstructions
            },
            training_materials: configData.training_materials || []
          },
          active: configData.moduleActive === true ? 1 : 0
        };
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'concerns_and_queries', configuration: validationConfig});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        
        // Validate the uploaded configuration
        if (!configData.concerns || !configData.queries) {
          throw new Error('Invalid configuration format');
        }

        // Update the state with uploaded configuration
        if (configData.concernsData) {
          setConcernsData(configData.concernsData);
        }
        
        if (configData.concernsInstructions) {
          setConcernsInstructions(configData.concernsInstructions);
        }
        
        if (configData.queriesInstructions) {
          setQueriesInstructions(configData.queriesInstructions);
        }
        
        if (configData.concerns) {
          setConcerns(configData.concerns);
        }
        
        if (configData.queries) {
          setQueries(configData.queries);
        }
        
        if (configData.moduleActive !== undefined) {
          setModuleActive(configData.moduleActive);
        }

        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false);
      } catch (error) {
        console.error('Error parsing uploaded configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false);
      }
    };
    
    reader.readAsText(file);
  };

  const convertToSeconds = (time) => {
    if(typeof time === 'string'){
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    return time;
  }
  const handleSourceClick = (source, segments) => {
    let start, end, message_id;
    if(source.start_timestamp && source.end_timestamp){
      // Set flag to prevent automatic segment selection
      sourceClickInProgressRef.current = true;
      
      // Convert timestamps to seconds with proper validation
      try {
        if(typeof source.start_timestamp === 'string'){
          start = convertToSeconds(source.start_timestamp)-1;
        }else{
          start = parseFloat(source.start_timestamp)-1;
        }
        if(typeof source.end_timestamp === 'string'){
          end = convertToSeconds(source.end_timestamp)+1;
        }else{
          end = parseFloat(source.end_timestamp)+1;
        }
        
        // Validate that start and end are valid numbers
        if (isNaN(start) || isNaN(end)) {
          console.warn('Invalid timestamp values:', source.start_timestamp, source.end_timestamp);
          sourceClickInProgressRef.current = false;
          return;
        }
        
        // Ensure start time is always less than end time
        if (start >= end) {
          console.warn('Start time must be less than end time:', start, end);
          sourceClickInProgressRef.current = false;
          return;
        }
        
      } catch (error) {
        console.warn('Error converting timestamps:', error);
        sourceClickInProgressRef.current = false;
        return;
      }
      
      // Handle audio case
      // Check if audio element exists and is valid
      if (!audioRef.current) {
        console.warn('Audio element not available');
        sourceClickInProgressRef.current = false;
        return;
      }

      try {
        // Pause any currently playing audio first
        audioRef.current.pause();
        
        // Remove any existing timeupdate listeners
        if (audioRef.current._currentTimeUpdateHandler) {
          audioRef.current.removeEventListener('timeupdate', audioRef.current._currentTimeUpdateHandler);
          audioRef.current._currentTimeUpdateHandler = null;
        }
        
        // Create new timeupdate handler
        const timeUpdateHandler = () => {
          if (!audioRef.current) return;
          
          const currentTime = audioRef.current.currentTime;
          
          if (currentTime >= end) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
          }
        };
        
        // Store reference to handler for cleanup
        audioRef.current._currentTimeUpdateHandler = timeUpdateHandler;
        
        // Add the new listener
        audioRef.current.addEventListener('timeupdate', timeUpdateHandler);
        
        // Seek to start time
        audioRef.current.currentTime = start;
        
        // Play with proper error handling
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Find the segment index whose range includes the start time
              let idx = 0;
              for (let i = 0; i < segments.length; i++) {
                if (start >= segments[i].start_time && start <= segments[i].end_time) {
                  idx = i;
                  break;
                }
              }
              setSelectedSegmentIdx(idx);
              
              // Scroll the segment into view
              if (segmentRefs.current[idx] && idx === selectedSegmentIdx) {
                segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
              
              // Clear the flag after successful playback start
              sourceClickInProgressRef.current = false;
            })
            .catch(error => {
              console.warn('Error playing audio:', error);
              // Clean up listener on error
              audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
              audioRef.current._currentTimeUpdateHandler = null;
              // Clear the flag on error
              sourceClickInProgressRef.current = false;
            });
        }
      } catch (error) {
        console.warn('Error handling source click:', error);
        sourceClickInProgressRef.current = false;
      }
    } else {
      // Handle chat message case
      message_id = source.segment_id;
      if(message_id){
        const message = segments.find(message => message.message_id === message_id);
        if(message){
          // Find the index of the message in chatTranscriptions
          const messageIndex = segments.findIndex(msg => msg.message_id === message_id);
          if (messageIndex !== -1) {
            // Create a ref for the message element if it doesn't exist
            if (!segmentRefs.current[messageIndex]) {
              segmentRefs.current[messageIndex] = React.createRef();
            }
            
            // Scroll the message into view
            setTimeout(() => {
              const messageElement = document.querySelector(`[data-message-id="${message_id}"]`);
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
                // Add a temporary highlight effect
                messageElement.style.backgroundColor = "rgba(111, 111, 254, 0.1)";
                setTimeout(() => {
                  messageElement.style.backgroundColor = "";
                }, 2000);
              }
            }, 100);
          }
        }
      }
    }
  }
  
  const [queriesData, setQueriesData] = useState({
    category: "Treatment & Medical Queries",
    subcategory: "Success Rates",
    example: "Patient is inquiring about the success rates for specific fertility treatments offered at our clinic."
  });
  
  const [notes, setNotes] = useState(
    "Always verify the patient's identity before discussing any medical or appointment information. For questions about success rates, refer to the official clinic statistics and avoid making promises about outcomes."
  );
  const audioRef = useRef()
  const segmentRefs = useRef([]);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);
  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };  
  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if(!link){
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = item.start_time; // Seek to the start time of the segment
      audioRef.current.play(); // Play the audio
    }
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
      return ""
    }
  
    const date = new Date(epochMillis);
    return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
  }
  const handleAudioTimeUpdate = (segments) => {
    if (sourceClickInProgressRef.current) return;
    const currentTime = audioRef.current.currentTime;

    // Find the segment corresponding to the current audio time
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
        if (segmentRefs.current[idx]) {
          // Scroll the corresponding segment into view if it's not already visible
          segmentRefs.current[idx].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });

          // Highlight the selected segment
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"4px solid #6f6ffe"
      case 'agent':
        return"4px solid #3B82F6"
      case 'bot':
        return"4px solid gray"
      case 'system':
        return "4px solid gray"
      default:
        break;
    }
  }
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"rgb(249, 250, 251)"
      case 'agent':
        return"rgb(249, 250, 251)"
      case 'bot':
        return"rgb(249, 250, 251)"
      case 'system':
        return"rgb(249, 250, 251)"
      default:
        break;
    }
  }
  
  // Sample data for preview
  const [samples, setSamples] = useState([]);
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false)
  }

  // Training Materials helpers (Concerns/Queries scoped)
  const openAddTrainingMaterial = () => {
    setEditingTrainingIndex(null);
    setSelectedTrainingParamDisplay('');
    setSelectedTrainingParamKey('');
    setMaterialRows([{ name: '', link: '' }]);
    setShowTrainingMaterialForm(true);
    setTimeout(() => {
      trainingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const openEditTrainingMaterial = (idx) => {
    const tm = trainingMaterials[idx];
    if (!tm) return;
    setEditingTrainingIndex(idx);
    setSelectedTrainingParamDisplay(tm.parameter_display_name || '');
    setSelectedTrainingParamKey(tm.parameter_key || '');
    setMaterialRows(Array.isArray(tm.materials) && tm.materials.length
      ? tm.materials.map(m => ({ name: m.name || m.material_name || '', link: m.link || m.material_link || '' }))
      : [{ name: '', link: '' }]
    );
    setShowTrainingMaterialForm(true);
    setTimeout(() => {
      trainingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  const handleRemoveTrainingMaterial = (idx) => {
    setTrainingMaterials(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const addMaterialRow = () => {
    setMaterialRows(prev => [...prev, { name: '', link: '' }]);
  };

  const removeMaterialRow = (idx) => {
    setMaterialRows(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const handleMaterialRowChange = (idx, field, value) => {
    setMaterialRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const saveTrainingMaterial = () => {
    try {
      if (!selectedTrainingParamDisplay) {
        showToast('Please select Concerns or Queries', 'error');
        return;
      }
      const key = selectedTrainingParamDisplay === 'Concerns' ? 'concerns' : 'queries';
      const cleaned = materialRows
        .map(m => ({ name: (m.name || '').trim(), link: (m.link || '').trim() }))
        .filter(m => m.name !== '' || m.link !== '');
      if (cleaned.length === 0) {
        showToast('Please add at least one material with name or link', 'error');
        return;
      }
      const item = {
        parameter_key: key,
        parameter_display_name: selectedTrainingParamDisplay,
        materials: cleaned
      };
      setTrainingMaterials(prev => {
        const copy = [...prev];
        const existingIdx = editingTrainingIndex !== null ? editingTrainingIndex : copy.findIndex(t => t.parameter_key === key);
        if (existingIdx !== -1) {
          copy[existingIdx] = item;
          return copy;
        }
        return [...copy, item];
      });
      setShowTrainingMaterialForm(false);
      setEditingTrainingIndex(null);
      setSelectedTrainingParamDisplay('');
      setSelectedTrainingParamKey('');
      setMaterialRows([]);
      showToast('Training material saved', 'success');
    } catch (e) {
      showToast('Failed to save training material', 'error');
    }
  };

  const serializeTrainingMaterials = () => {
    try {
      const obj = {};
      (trainingMaterials || []).forEach((item) => {
        const key = item?.parameter_key === 'queries' ? 'queries' : 'concerns';
        const mats = Array.isArray(item?.materials) ? item.materials : [];
        const cleaned = mats
          .map(m => ({ name: (m?.name || m?.material_name || '').trim(), link: (m?.link || m?.material_link || '').trim() }))
          .filter(m => m.name !== '' || m.link !== '');
        obj[key] = {
          display_name: item?.parameter_display_name || (key === 'concerns' ? 'Concerns' : 'Queries'),
          materials: cleaned
        };
      });
      return obj;
    } catch (e) {
      return {};
    }
  };

  // Functions for hierarchical structure management
  const handleToggleExpand = (type, id) => {
    if (type === 'concern') {
      setConcerns(concerns.map(concern => 
        concern.id === id ? { ...concern, expanded: !concern.expanded } : concern
      ));
    } else if (type === 'query') {
      setQueries(queries.map(query => 
        query.id === id ? { ...query, expanded: !query.expanded } : query
      ));
    }
  };

  const handleAddConcern = () => {
    if (newConcern.trim() === "") return;
    
    setConcerns([
      ...concerns,
      {
        id: Math.max(0, ...concerns.map(c => c.id)) + 1,
        name: newConcern,
        expanded: true,
        subconcerns: []
      }
    ]);
    
    setNewConcern("");
  };

  const handleAddQuery = () => {
    if (newQuery.trim() === "") return;
    
    setQueries([
      ...queries,
      {
        id: Math.max(0, ...queries.map(q => q.id)) + 1,
        name: newQuery,
        expanded: true,
        subqueries: []
      }
    ]);
    
    setNewQuery("");
  };

  const handleAddSubconcern = (concernId) => {
    if (newSubconcern.name.trim() === "") return;
    
    setConcerns(concerns.map(concern => 
      concern.id === concernId ? 
        { 
          ...concern, 
          subconcerns: [
            ...concern.subconcerns, 
            { 
              id: Math.max(0, ...concern.subconcerns.map(sc => sc.id), 0) + 1, 
              name: newSubconcern.name 
            }
          ] 
        } : concern
    ));
    
    setNewSubconcern({
      concernId: null,
      name: ""
    });
  };

  const handleAddSubquery = (queryId) => {
    if (newSubquery.name.trim() === "") return;
    
    setQueries(queries.map(query => 
      query.id === queryId ? 
        { 
          ...query, 
          subqueries: [
            ...query.subqueries, 
            { 
              id: Math.max(0, ...query.subqueries.map(sq => sq.id), 0) + 1, 
              name: newSubquery.name 
            }
          ] 
        } : query
    ));
    
    setNewSubquery({
      queryId: null,
      name: ""
    });
  };

  const handleRemoveConcern = (id) => {
    setConcerns(concerns.filter(concern => concern.id !== id));
  };

  const handleRemoveQuery = (id) => {
    setQueries(queries.filter(query => query.id !== id));
  };

  const handleRemoveSubconcern = (concernId, subconcernId) => {
    setConcerns(concerns.map(concern => 
      concern.id === concernId ? 
        { 
          ...concern, 
          subconcerns: concern.subconcerns.filter(sc => sc.id !== subconcernId) 
        } : concern
    ));
  };

  const handleRemoveSubquery = (queryId, subqueryId) => {
    setQueries(queries.map(query => 
      query.id === queryId ? 
        { 
          ...query, 
          subqueries: query.subqueries.filter(sq => sq.id !== subqueryId) 
        } : query
    ));
  };

  const startEdit = (type, id, name, parentId = null) => {
    setEditMode({ type, id, name, parentId });
  };

  const cancelEdit = () => {
    setEditMode({ type: null, id: null, name: "", parentId: null });
  };

  const saveEdit = () => {
    if (editMode.name.trim() === "") {
      cancelEdit();
      return;
    }
    
    if (editMode.type === "concern") {
      setConcerns(concerns.map(concern => 
        concern.id === editMode.id ? { ...concern, name: editMode.name } : concern
      ));
    } else if (editMode.type === "subconcern") {
      setConcerns(concerns.map(concern => 
        concern.id === editMode.parentId ? 
          { 
            ...concern, 
            subconcerns: concern.subconcerns.map(sc => 
              sc.id === editMode.id ? { ...sc, name: editMode.name } : sc
            ) 
          } : concern
      ));
    } else if (editMode.type === "query") {
      setQueries(queries.map(query => 
        query.id === editMode.id ? { ...query, name: editMode.name } : query
      ));
    } else if (editMode.type === "subquery") {
      setQueries(queries.map(query => 
        query.id === editMode.parentId ? 
          { 
            ...query, 
            subqueries: query.subqueries.map(sq => 
              sq.id === editMode.id ? { ...sq, name: editMode.name } : sq
            ) 
          } : query
      ));
    }
    
    cancelEdit();
  };

  const handleConcernsChange = (field, value) => {
    setConcernsData({
      ...concernsData,
      [field]: value
    });
  };

  const handleQueriesChange = (field, value) => {
    setQueriesData({
      ...queriesData,
      [field]: value
    });
  };

  const [email, setEmail] = useState(null)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [moduleName, setModuleName] = useState(null)
  const [concernsModule, setConcernsModule] = useState(null)
  const [defaultConfig, setDefaultConfig] = useState(null)

  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const getDefaultModuleConfigs = async () => {
    try {
      setLoading(true);
      let res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if (resp.data){
          setDefaultConfig(resp.data.configuration)
        }
      }
    setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const getModuleConfig = async() => {
    try {
      setLoading(true)
      let res = await server.getClientModuleConfig(client_id, service_id,email,'apps',undefined, encodeURIComponent(moduleName));
      if(res.status === 200){
        let resp = await res.json()
        setConcernsModule(resp.data.configuration)
        setModuleActive(resp.data.active===1 ? true : false)
        setModuleSaveDraftConfiguration(resp.data.configuration)
        setCurrent_version(resp.data.version)
        
        // Initialize hierarchical structure from API response
        const config = resp.data.configuration;
        // Load training materials if present
        if (config?.training_materials) {
          try {
            const tm = config.training_materials;
            const list = Object.keys(tm).map((k) => {
              const entry = tm[k] || {};
              const mats = Array.isArray(entry.materials) ? entry.materials : [];
              return {
                parameter_key: k,
                parameter_display_name: entry.display_name || (k === 'queries' ? 'Queries' : 'Concerns'),
                materials: mats.map(m => ({ name: m.name || m.material_name || '', link: m.link || m.material_link || '' }))
              };
            });
            setTrainingMaterials(list);
          } catch (_) {}
        } else {
          setTrainingMaterials([]);
        }
        setConcernsInstructions(config.concerns?.instruction || "");
        setQueriesInstructions(config.queries?.instruction || "");

        // Transform concerns data
        if (config.concerns?.categories) {
          const formattedConcerns = Object.entries(config.concerns.categories).map(([key, value], index) => ({
            id: index + 1,
            name: key,
            expanded: true,
            subconcerns: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
              id: (index + 1) * 100 + subIndex + 1,
              name: sub
            })) : []
          }));
          setConcerns(formattedConcerns);
        }
        
        // Transform queries data
        if (config.queries?.categories) {
          const formattedQueries = Object.entries(config.queries.categories).map(([key, value], index) => ({
            id: index + 1,
            name: key,
            expanded: true,
            subqueries: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
              id: (index + 1) * 100 + subIndex + 1,
              name: sub
            })) : []
          }));
          setQueries(formattedQueries);
        }
        
        // setDefaultConfig(resp.data.default.configuration)
      }
    setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleGenerateSample = async(sample_flag) => {
    try {
      setLoading(true)
      let config = {'active': moduleActive===true ? 1 : 0, 'configuration': getTransformedConfiguration()}
      let res = await server.generateSampleModuleOutputs({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: "concerns_and_queries", configuration: config, 'sample_set_flag': sample_flag});
      if(res.status === 200){
        let resp = await res.json()
        setSamples(resp.data)
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleDeployModule = async(versionDescription) => {
    try {
      setLoading(true)
      let deployed = false;
      let errorMessage = 'Failed to deploy module';
      let config = {'active': moduleActive===true ? 1 : 0, 'configuration': getTransformedConfiguration()}
      let res = await server.deployModule({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: "concerns_and_queries", configuration: config, default: defaultActive, 'version_changed': current_version, version_description: versionDescription});
      if(res.status === 200){
        showToast('Module deployed successfully', "success");
        deployed = true;
      }else{
        try {
          const errData = await res.clone().json();
          errorMessage = errData?.message || errData?.error || errorMessage;
        } catch (_) {}
      }
      if (deployed) {
        setCloseModal()
      } else {
        showToast(errorMessage, 'error');
        setLoading(false)
      }
      setLoading(false)
    } catch (error) {
      showToast('Failed to deploy module', 'error');
      setLoading(false)
    }
  }
  const getProjectStatus = async () => {
    try {
      let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data && resp.data === 'completed'){
          setShowSampleTest(true);
        }
      }
    } catch (error) {
    }
  }

  const resetToDefault = () => {
    setDefaultActive(true)
    setConcernsModule(defaultConfig)
    setModuleActive(true)
    // Initialize hierarchical structure from default config
    if (defaultConfig) {
      setConcernsInstructions(defaultConfig.concerns?.instruction || "");
      setQueriesInstructions(defaultConfig.queries?.instruction || "");
      
      // Transform concerns data
      if (defaultConfig.concerns?.categories) {
        const formattedConcerns = Object.entries(defaultConfig.concerns.categories).map(([key, value], index) => ({
          id: index + 1,
          name: key,
          expanded: true,
          subconcerns: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
            id: (index + 1) * 100 + subIndex + 1,
            name: sub
          })) : []
        }));
        setConcerns(formattedConcerns);
      }
      
      // Transform queries data
      if (defaultConfig.queries?.categories) {
        const formattedQueries = Object.entries(defaultConfig.queries.categories).map(([key, value], index) => ({
          id: index + 1,
          name: key,
          expanded: true,
          subqueries: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
            id: (index + 1) * 100 + subIndex + 1,
            name: sub
          })) : []
        }));
        setQueries(formattedQueries);
      }
    }
  }
  const getModuleDraftSaveConfiguration = async () => {
    try {
      let res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data){
          setModuleSaveDraftConfiguration(resp.data.configuration)
          setConcernsModule(resp.data.configuration)
          setModuleActive(resp.data.active === 1 ? true : false)
          
          // Initialize hierarchical structure from draft
          const config = resp.data.configuration;
          setConcernsInstructions(config.concerns?.instruction || "");
          setQueriesInstructions(config.queries?.instruction || "");
          
          // Transform concerns data
          if (config.concerns?.categories) {
            const formattedConcerns = Object.entries(config.concerns.categories).map(([key, value], index) => ({
              id: index + 1,
              name: key,
              expanded: true,
              subconcerns: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
                id: (index + 1) * 100 + subIndex + 1,
                name: sub
              })) : []
            }));
            setConcerns(formattedConcerns);
          }
          
          // Transform queries data
          if (config.queries?.categories) {
            const formattedQueries = Object.entries(config.queries.categories).map(([key, value], index) => ({
              id: index + 1,
              name: key,
              expanded: true,
              subqueries: value.subcategories ? value.subcategories.map((sub, subIndex) => ({
                id: (index + 1) * 100 + subIndex + 1,
                name: sub
              })) : []
            }));
            setQueries(formattedQueries);
          }
        }else{
          showToast('No saved draft.', "error")
          setConfigType('Live Version')
        }
        // resetToDefault('draft')
      }
    } catch (error) {
    }
  }
  // Transform hierarchical data to API format
  const transformConcernsToApiFormat = () => {
    const concernsObj = {};
    concerns.forEach(concern => {
      concernsObj[concern.name] = {
        subcategories: concern.subconcerns.map(sub => sub.name.trim())
      };
    });
    return concernsObj;
  };

  const transformQueriesToApiFormat = () => {
    const queriesObj = {};
    queries.forEach(query => {
      queriesObj[query.name] = {
        subcategories: query.subqueries.map(sub => sub.name.trim())
      };
    });
    return queriesObj;
  };

  const getTransformedConfiguration = () => {
    return {
      concerns: {
        categories: transformConcernsToApiFormat()
        ,
        "instruction": concernsInstructions
      },
      queries: {
        categories: transformQueriesToApiFormat(),
        "instruction": queriesInstructions
      },
      training_materials: serializeTrainingMaterials(),
    };
  };

  const handleUpdateConfig = async () => {
    try {
      let config = {'configuration': getTransformedConfiguration(), 'active': moduleActive===true ? 1 : 0}
      let res = await server.postModuleDraftSaveConfiguration({client_id, service_id, email, module_name: 'concerns_and_queries', 'configuration': config, 'menu': 'apps', 'submenu': undefined})
      if(res.status === 200) {
        showToast(await getApiToastMessage(res, 'Draft saved successfully'), "success")
      } else {
        showToast(await getApiToastMessage(res, 'Failed to save draft'), "error")
      }
    } catch (error) {
      showToast('Failed to save draft', "error")
    }
  }
  useEffect(()=> {
    if(moduleSaveDraftConfiguration){
      let config = {'configuration': getTransformedConfiguration(), 'active': moduleActive===true ? 1 : 0}
      config_data(config)
    }
  }, [moduleSaveDraftConfiguration, moduleActive, concerns, queries, concernsInstructions, queriesInstructions])
  useEffect(()=> {
    if(configType === 'Saved Draft'){
      getModuleDraftSaveConfiguration();
    }
    if(configType === 'Default'){
      resetToDefault();
    }
    if(configType === 'Live Version' && client_id && service_id && moduleName && email){
      getModuleConfig();
      getDefaultModuleConfigs()
    }
  }, [configType])

  // Load new audio when sample changes
  useEffect(() => {
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user && clientID) {
          setEmail(user.email);
          setClient_id(clientID);
        }
        if (serviceID && module_name) {
          setService_id(serviceID);
          setModuleName(module_name)
        }
      }, [clientID, serviceID, module_name]);
    
      useEffect(() => {
        if (client_id && service_id && moduleName && email) {
          getModuleConfig();
          getDefaultModuleConfigs()
          getProjectStatus()
        }
      }, [client_id, service_id, moduleName, email]);
      const [showMoreMap, setShowMoreMap] = useState({}); // Initialize as empty object

  const toggleShowMore = (key) => {
    setShowMoreMap(prev => ({
      ...prev,
      [key]: !prev[key]  // toggle this particular key
    }));
  };
  
  const getCurrentConfigForDiff = () => getTransformedConfiguration();
  const getLoadedConfigForDiff = () => (
    configType === 'Default' ? defaultConfig : (concernsModule || moduleSaveDraftConfiguration)
  );

  // Configuration Screen
  if (!showPreview) {
    return (
      <div className="p-3">
        <div className="mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Concerns & Queries <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType.replace(/^Reset to /, '')}</span></h1>
              <p className="text-gray-500 text-sm mt-1">Configure and customise concerns and queries</p>
              
              {/* Configuration Import/Export Buttons */}
              {role === 'superadmin' && <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="config-upload"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm"
                    onClick={() => document.getElementById('config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>
                
                <button
                  type="button"
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>}
            </div>
            <div style={{display: "flex", alignItems: "center"}}>
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only global-input"
                    id="toggle-all"
                  />
                  <label 
                    htmlFor="toggle-all"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                  onClick={handleUpdateConfig}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </button>
                <ConfigurationDiffButton
                  title="Concerns & Queries Changes"
                  originalConfig={getLoadedConfigForDiff}
                  modifiedConfig={getCurrentConfigForDiff}
                />
                {showSampleTest && <button 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                onClick={() => setShowSampleTestModal(true)}
              >
                Manage Test Set
              </button>}
                <div className="relative inline-block mx-2">
                  <button
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                    onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                    type="button"
                  >
                    {configType}
                    <span className="ml-2">
                      {showConfigDropdown ? (
                        // Up arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        // Down arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {showConfigDropdown && (
                    <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {['Live Version', 'Saved Draft', 'Default'].map(option => (
                        <div
                          key={option}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                          onClick={() => {
                            setConfigType(option);
                            setShowConfigDropdown(false);
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* <ModernTimeDropdown width="w-full mx-2" placeholder="Select Configuration" value={configType} onChange={(e)=>setConfigType(e.target.value)} options={['Reset to Default', 'Reset to Saved Draft', 'New Configuration']}/> */}
                {/* <button 
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 mr-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={resetToDefault}
              >
                Reset to Default
              </button> */}
            <button 
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(true); handleGenerateSample(1);}}
            >
              Preview
            </button>
            </div>
          </div>
          

          <div style={{overflow: "auto", height: "68vh"}}>
            {!loading && (
              <>
                <div className="bg-white rounded-lg shadow mb-6">
                  <div className="border-b border-gray-200">
                    <nav className="flex" aria-label="Tabs">
                      <button
                        onClick={() => setActiveTab('concerns')}
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                          activeTab === 'concerns'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`} style={{lineHeight: "0.55rem", fontSize: "0.9rem"}}
                      >
                        Concerns
                      </button>
                      <button
                        onClick={() => setActiveTab('queries')}
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                          activeTab === 'queries'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`} style={{lineHeight: "0.55rem", fontSize: "0.9rem"}}
                      >
                        Queries
                      </button>
                      <button
                        onClick={() => setActiveTab('training_material')}
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                          activeTab === 'training_material'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`} style={{lineHeight: "0.55rem", fontSize: "0.9rem"}}
                      >
                        Training Material
                      </button>
                    </nav>
                  </div>

                  {activeTab === 'concerns' && (
                    <div className="p-6">
                      {/* Concerns Instructions */}
                      <div className="mb-6">
                        <label htmlFor="concerns-instructions" className="block text-sm font-medium text-gray-700 mb-2">
                          Instructions
                        </label>
                        <textarea
                          id="concerns-instructions"
                          rows="6"
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          value={concernsInstructions}
                          onChange={(e) => setConcernsInstructions(e.target.value)}
                          placeholder="Provide instructions for how concerns should be categorized..."
                        ></textarea>
                      </div>

                      {/* Concerns Categories & SubCategories */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Concerns Categories & SubCategories
                        </label>
                        
                        {/* Concerns List */}
                        <div className="space-y-3">
                          {concerns.map(concern => (
                            <div key={concern.id} className="border rounded-lg overflow-hidden">
                              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                                {editMode.type === "concern" && editMode.id === concern.id ? (
                                  <div className="flex-1 mr-2">
                                    <input
                                      type="text"
                                      className="w-full px-3 py-1 text-gray-700 border rounded-lg focus:outline-none global-input"
                                      value={editMode.name}
                                      onChange={(e) => setEditMode({...editMode, name: e.target.value})}
                                      autoFocus
                                      onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                                      onBlur={saveEdit}
                                    />
                                  </div>
                                ) : (
                                  <h3 className="font-medium text-gray-800 flex-1">{concern.name}</h3>
                                )}
                                <div className="flex items-center space-x-2">
                                  {editMode.type !== "concern" || editMode.id !== concern.id ? (
                                    <>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                        onClick={() => startEdit("concern", concern.id, concern.name)}
                                        title="Edit concern"
                                      >
                                        <Edit2 size={16} />
                                      </button>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-red-600 focus:outline-none" 
                                        onClick={() => handleRemoveConcern(concern.id)}
                                        title="Remove concern"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                        onClick={() => handleToggleExpand('concern', concern.id)}
                                        title={concern.expanded ? "Collapse" : "Expand"}
                                      >
                                        {concern.expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                      </button>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              
                              {concern.expanded && (
                                <div className="p-4 space-y-3">
                                  {/* Subconcerns List */}
                                  <div className="space-y-2 ml-4">
                                    {concern.subconcerns.map(subconcern => (
                                      <div key={subconcern.id} className="flex items-center justify-between group">
                                        {editMode.type === "subconcern" && editMode.id === subconcern.id ? (
                                          <div className="flex-1 mr-2">
                                            <input
                                              type="text"
                                              className="w-full px-3 py-1 text-gray-700 border rounded-lg focus:outline-none global-input"
                                              value={editMode.name}
                                              onChange={(e) => setEditMode({...editMode, name: e.target.value})}
                                              autoFocus
                                              onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                                              onBlur={saveEdit}
                                            />
                                          </div>
                                        ) : (
                                          <span className="text-gray-700 flex-1">{subconcern.name}</span>
                                        )}
                                        
                                        {editMode.type !== "subconcern" || editMode.id !== subconcern.id ? (
                                          <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                              className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                              onClick={() => startEdit("subconcern", subconcern.id, subconcern.name, concern.id)}
                                              title="Edit subconcern"
                                            >
                                              <Edit2 size={14} />
                                            </button>
                                            <button 
                                              className="p-1 text-gray-500 hover:text-red-600 focus:outline-none" 
                                              onClick={() => handleRemoveSubconcern(concern.id, subconcern.id)}
                                              title="Remove subconcern"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Add Subconcern */}
                                  {newSubconcern.concernId === concern.id ? (
                                    <div className="flex items-center mt-2 ml-4">
                                      <input
                                        type="text"
                                        className="flex-1 px-3 py-1 text-sm text-gray-700 border rounded-lg focus:outline-none global-input"
                                        placeholder="Enter subconcern name"
                                        value={newSubconcern.name}
                                        onChange={(e) => setNewSubconcern({...newSubconcern, name: e.target.value})}
                                        onKeyPress={(e) => e.key === "Enter" && handleAddSubconcern(concern.id)}
                                      />
                                      <button 
                                        className="ml-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 focus:outline-none global-input"
                                        onClick={() => handleAddSubconcern(concern.id)}
                                      >
                                        Add
                                      </button>
                                      <button 
                                        className="ml-1 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        onClick={() => setNewSubconcern({concernId: null, name: ""})}
                                      >
                                        <FaTimes style={{color: "slategray"}}/>
                                      </button>
                                    </div>
                                  ) : (
                                    <button 
                                      className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 mt-2 ml-4 focus:outline-none"
                                      onClick={() => setNewSubconcern({concernId: concern.id, name: ""})}
                                    >
                                      <PlusCircle size={16} className="mr-1" />
                                      Add Subconcern
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Add New Concern */}
                        <div className="mt-4">
                          <div className="flex items-center">
                            <input
                              type="text"
                              className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                              placeholder="Enter new concern name"
                              value={newConcern}
                              onChange={(e) => setNewConcern(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && handleAddConcern()}
                            />
                            <button 
                              className="ml-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none global-input"
                              onClick={handleAddConcern}
                            >
                              Add Concern
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'training_material' && (
                    <div className="p-6">
                      <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                        <div className="flex justify-between items-center mb-4">
                          <h2 className="text-lg font-medium text-gray-800">Training Material</h2>
                          <button 
                            className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 mr-2" style={{height: "40px", borderRadius: "4px"}}
                            onClick={openAddTrainingMaterial}
                          >
                            Add Material
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <ConfigurationTableSearch label="Search training material by parameter name" value={tableSearch.training} onChange={value => setTableSearch({ ...tableSearch, training: value })} />
                          <table className="configuration-table divide-y divide-gray-200">
                            <colgroup>
                              <col style={{ width: "22%" }} />
                              <col />
                              <col style={{ width: "120px" }} />
                            </colgroup>
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {trainingMaterials.map((tm, idx) => matchesParameterName(tm, tableSearch.training) && (
                                <tr key={tm.parameter_key || idx}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tm.parameter_display_name || tm.parameter_key}</td>
                                  <td className="px-6 py-4 text-sm text-gray-500">{Array.isArray(tm.materials) && tm.materials.length > 0 ? tm.materials.map(m => m.link).filter(Boolean).join(', ') : '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button 
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 text-md"
                                      onClick={() => openEditTrainingMaterial(idx)}
                                    >
                                      <FaRegEdit size={16}/>
                                    </button>
                                    <button 
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 text-md"
                                      onClick={() => handleRemoveTrainingMaterial(idx)}
                                    >
                                      <MdDelete size={16}/>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {showTrainingMaterialForm && (
                        <div ref={trainingFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6" style={{width: "85%", margin: "auto", marginTop: "1rem"}}>
                          <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-medium text-gray-800">{editingTrainingIndex !== null ? 'Edit Training Material' : 'Add Training Material'}</h3>
                            <button 
                              onClick={() => { setShowTrainingMaterialForm(false); setEditingTrainingIndex(null); setSelectedTrainingParamDisplay(''); setSelectedTrainingParamKey(''); setMaterialRows([]); }}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <FaTimes style={{color: "slategray"}}/>
                            </button>
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Parameter</label>
                            <ModernTimeDropdown 
                              width="w-full" 
                              placeholder="Select Parameter" 
                              value={selectedTrainingParamDisplay} 
                              options={['Concerns', 'Queries']} 
                              onChange={(e) => {
                                const label = e.target.value;
                                setSelectedTrainingParamDisplay(label);
                                setSelectedTrainingParamKey(label === 'Queries' ? 'queries' : 'concerns');
                              }} 
                            />
                          </div>

                          <div className="mb-2 flex justify-between items-center">
                            <label className="block text-sm font-medium text-gray-700">Materials</label>
                            <button onClick={addMaterialRow} className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Add Link</button>
                          </div>
                          <div className="space-y-2">
                            {materialRows.map((row, idx) => (
                              <div key={idx} className="flex gap-2">
                                <input 
                                  type="text" 
                                  className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" 
                                  placeholder="Material Name" 
                                  value={row.name} 
                                  onChange={(e) => handleMaterialRowChange(idx, 'name', e.target.value)}
                                />
                                <input 
                                  type="text" 
                                  className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" 
                                  placeholder="Material Link" 
                                  value={row.link} 
                                  onChange={(e) => handleMaterialRowChange(idx, 'link', e.target.value)}
                                />
                                <button onClick={() => removeMaterialRow(idx)} className="px-2 py-1 text-gray-700 rounded-md hover:bg-gray-50"><MdDelete size={18} /></button>
                              </div>
                            ))}
                          </div>

                          <div className="flex justify-end gap-3 mt-4">
                            <button
                              onClick={() => { setShowTrainingMaterialForm(false); setEditingTrainingIndex(null); setSelectedTrainingParamDisplay(''); setSelectedTrainingParamKey(''); setMaterialRows([]); }}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveTrainingMaterial}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'queries' && (
                    <div className="p-6">
                      {/* Queries Instructions */}
                      <div className="mb-6">
                        <label htmlFor="queries-instructions" className="block text-sm font-medium text-gray-700 mb-2">
                          Instructions
                        </label>
                        <textarea
                          id="queries-instructions"
                          rows="6"
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          value={queriesInstructions}
                          onChange={(e) => setQueriesInstructions(e.target.value)}
                          placeholder="Provide instructions for how queries should be categorized..."
                        ></textarea>
                      </div>

                      {/* Queries Categories & SubCategories */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Queries Categories & SubCategories
                        </label>
                        
                        {/* Queries List */}
                        <div className="space-y-3">
                          {queries.map(query => (
                            <div key={query.id} className="border rounded-lg overflow-hidden">
                              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                                {editMode.type === "query" && editMode.id === query.id ? (
                                  <div className="flex-1 mr-2">
                                    <input
                                      type="text"
                                      className="w-full px-3 py-1 text-gray-700 border rounded-lg focus:outline-none global-input"
                                      value={editMode.name}
                                      onChange={(e) => setEditMode({...editMode, name: e.target.value})}
                                      autoFocus
                                      onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                                      onBlur={saveEdit}
                                    />
                                  </div>
                                ) : (
                                  <h3 className="font-medium text-gray-800 flex-1">{query.name}</h3>
                                )}
                                <div className="flex items-center space-x-2">
                                  {editMode.type !== "query" || editMode.id !== query.id ? (
                                    <>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                        onClick={() => startEdit("query", query.id, query.name)}
                                        title="Edit query"
                                      >
                                        <Edit2 size={16} />
                                      </button>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-red-600 focus:outline-none" 
                                        onClick={() => handleRemoveQuery(query.id)}
                                        title="Remove query"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                      <button 
                                        className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                        onClick={() => handleToggleExpand('query', query.id)}
                                        title={query.expanded ? "Collapse" : "Expand"}
                                      >
                                        {query.expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                      </button>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              
                              {query.expanded && (
                                <div className="p-4 space-y-3">
                                  {/* Subqueries List */}
                                  <div className="space-y-2 ml-4">
                                    {query.subqueries.map(subquery => (
                                      <div key={subquery.id} className="flex items-center justify-between group">
                                        {editMode.type === "subquery" && editMode.id === subquery.id ? (
                                          <div className="flex-1 mr-2">
                                            <input
                                              type="text"
                                              className="w-full px-3 py-1 text-gray-700 border rounded-lg focus:outline-none global-input"
                                              value={editMode.name}
                                              onChange={(e) => setEditMode({...editMode, name: e.target.value})}
                                              autoFocus
                                              onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                                              onBlur={saveEdit}
                                            />
                                          </div>
                                        ) : (
                                          <span className="text-gray-700 flex-1">{subquery.name}</span>
                                        )}
                                        
                                        {editMode.type !== "subquery" || editMode.id !== subquery.id ? (
                                          <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                              className="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                                              onClick={() => startEdit("subquery", subquery.id, subquery.name, query.id)}
                                              title="Edit subquery"
                                            >
                                              <Edit2 size={14} />
                                            </button>
                                            <button 
                                              className="p-1 text-gray-500 hover:text-red-600 focus:outline-none" 
                                              onClick={() => handleRemoveSubquery(query.id, subquery.id)}
                                              title="Remove subquery"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Add Subquery */}
                                  {newSubquery.queryId === query.id ? (
                                    <div className="flex items-center mt-2 ml-4">
                                      <input
                                        type="text"
                                        className="flex-1 px-3 py-1 text-sm text-gray-700 border rounded-lg focus:outline-none global-input"
                                        placeholder="Enter subquery name"
                                        value={newSubquery.name}
                                        onChange={(e) => setNewSubquery({...newSubquery, name: e.target.value})}
                                        onKeyPress={(e) => e.key === "Enter" && handleAddSubquery(query.id)}
                                      />
                                      <button 
                                        className="ml-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 focus:outline-none global-input"
                                        onClick={() => handleAddSubquery(query.id)}
                                      >
                                        Add
                                      </button>
                                      <button 
                                        className="ml-1 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        onClick={() => setNewSubquery({queryId: null, name: ""})}
                                      >
                                        <FaTimes style={{color: "slategray"}}/>
                                      </button>
                                    </div>
                                  ) : (
                                    <button 
                                      className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 mt-2 ml-4 focus:outline-none"
                                      onClick={() => setNewSubquery({queryId: query.id, name: ""})}
                                    >
                                      <PlusCircle size={16} className="mr-1" />
                                      Add Subquery
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Add New Query */}
                        <div className="mt-4">
                          <div className="flex items-center">
                            <input
                              type="text"
                              className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                              placeholder="Enter new query name"
                              value={newQuery}
                              onChange={(e) => setNewQuery(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && handleAddQuery()}
                            />
                            <button 
                              className="ml-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none global-input"
                              onClick={handleAddQuery}
                            >
                              Add Query
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {
          showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='concerns_and_queries' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )
        }
      </div>
    );
  }
  
  // Preview Screen
  return (
    <div className="p-3">
      <div className="mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Concerns & Queries</h1>
            <p className="text-gray-500 text-sm mt-1">Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(false); setExpandedSample(null); setSamples([])}}
            >
              Go Back
            </button>
            <button onClick={() => {handleGenerateSample(0); setExpandedSample(null); setSamples([])}} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Concern Insight Changes"
              originalConfig={getLoadedConfigForDiff}
              modifiedConfig={getCurrentConfigForDiff}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'concerns_and_queries'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            handleDeployModule(versionDescription);
          }}
        />

        <div className="bg-white rounded-lg mb-6" style={{gap: "10px", display: "flex", flexDirection: "column", height: "75vh", overflow: "auto"}}>
          {
            loading && (
              <div className="flex justify-center items-center" style={{ height: "50vh", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>
            )
          }
          {!loading && samples.map((sample) => (
            <div key={sample.call_id} className="border-gray-200 last:border-0">
              <div  onClick={() => setExpandedSample(expandedSample === sample.call_id ? null : sample.call_id)} className='p-4 border-b rounded-lg border-gray-200 bg-gray-50 cursor-pointer'>
              <div 
                className="flex justify-between items-center "
               
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">{sample.name}</span>
                </div>
                <svg 
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSample === sample.call_id ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {sample.call_id} | Time: {sample.call_time} | Employee: {sample.employee_name} | Record Type: {sample.record_type}
                      </div>
              </div>
              
              {expandedSample === sample.call_id && (
                <div className='p-4 border rounded-lg flex'>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
                            <div 
                              className="p-2 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center" style={{justifyContent: "space-between"}}
                            >
                              
                              <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                                </svg>
                                <h3 className="font-medium text-gray-800">Output</h3>
                              </div>

                            </div>
                            <div className="border shadow rounded-lg border-gray-200 m-4" style={{overflow: 'auto', height: "50vh"}}>
                  {sample['processed_analytics']['concerns_and_queries']['ans'] && (
                    <FeedbackSection key="parameters" showMoreMap={showMoreMap} toggleShowMore={toggleShowMore} source_click={(source)=>handleSourceClick(source, sample.segments)}
                  dataKey="Concern & Queries" data={sample['processed_analytics']['concerns_and_queries']['ans']} maxHeight="45vh" />
                  )}
                </div>
                    </div>
                  {/* Audio Section */}
                  <div className="ml-2 border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.35"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
                              <div className="flex items-center">
                              {sample.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                                <h3 className="font-medium text-gray-800">{sample.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
                              </div>
                            </div>
                            {sample.record_link && <div className="p-2 bg-white">
                              <audio controls key={sample.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(sample.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                                <source src={sample.record_link} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>}
                            <hr/>
                            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', minHeight: "30vh", maxHeight: "40vh"}}>
                            {
                              sample.record_type === 'call' && Array.isArray(sample.segments) && sample.segments.map((item, idx)=> {
                                        const isSelected = selectedSegmentIdx === idx;
                                        return (
                                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, sample.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start_time)}</div>
                                            </div>
                                            <div style={{fontSize: "15px"}}>{item.text}</div>
                                          </div>
                                        )
                                      })
                                    }
                            {
                              sample.record_type === 'chat' && Array.isArray(sample.segments) && sample.segments.map((item, idx)=> {
                                return (
                                  <div key={`segment_${idx}`}
                                  data-message-id={item.message_id}
                                  style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                      <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                      </div>
                                      <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                    </div>
                                    <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                                    {item.content.image && item.content.image.url && 
                                      <div style={{fontSize: "15px", whiteSpace: "pre-line"}}>
                                        <br/>
                                       Image Shared - {item.content.image.url}
                                      </div>
                                      }
                                    {
                                      item.content.buttons && item.content.buttons.length > 0 && (
                                        <div>
                                          <br/>
                                          {item.actor_type === 'user' ? (
                                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          ) : (
                                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          )}
                                        </div>
                                      )
                                    }
                                  </div>
                                )
                                      })
                                    }
                            </div>
                          </div>
                          
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConcernInsightConfigure;
